/**
 * AME Vehicle Lift Configurator — guided quiz for vehicle lift selection
 * ES5 IIFE, no dependencies, deferred loading
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ame-vl-configurator]');
  if (!root) return;

  // --- DOM refs ---
  var ctaBtn = root.querySelector('[data-vl-cfg-cta]');
  var wizard = root.querySelector('#ame-vl-cfg-wizard');
  var resultsPanel = root.querySelector('[data-vl-cfg-results]');
  var resultsGrid = root.querySelector('[data-vl-cfg-results-grid]');
  var resultsHeading = root.querySelector('[data-vl-cfg-results-heading]');
  var resultsSubtitle = root.querySelector('[data-vl-cfg-results-subtitle]');
  var viewAllLink = root.querySelector('[data-vl-cfg-view-all]');
  var progressLabel = root.querySelector('[data-vl-cfg-progress-label]');
  var progressBar = root.querySelector('[data-vl-cfg-progressbar]');
  var progressFill = root.querySelector('[data-vl-cfg-progress-fill]');
  var liveRegion = root.querySelector('[data-vl-cfg-live]');

  var steps = root.querySelectorAll('[data-vl-cfg-step]');
  var TOTAL_STEPS = steps.length;

  // --- State ---
  var currentStep = 1;
  var answers = {};
  var products = [];

  // --- Parse product data ---
  var dataEl = document.getElementById('ame-vl-cfg-data');
  if (dataEl) {
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      products = [];
    }
  }

  // --- Session storage helpers ---
  function saveState() {
    try {
      sessionStorage.setItem('ame-vl-cfg-state', JSON.stringify({
        step: currentStep,
        answers: answers
      }));
    } catch (e) { /* ignore */ }
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem('ame-vl-cfg-state');
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return null;
  }

  function clearState() {
    try { sessionStorage.removeItem('ame-vl-cfg-state'); } catch (e) { /* ignore */ }
  }

  // --- Announce to screen readers ---
  function announce(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  // --- Progress bar ---
  function updateProgress(step) {
    var pct = Math.round((step / TOTAL_STEPS) * 100);
    if (progressLabel) progressLabel.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;
    if (progressFill) progressFill.style.width = pct + '%';
    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', pct);
    }
  }

  // --- Show/hide steps ---
  function showStep(n) {
    for (var i = 0; i < steps.length; i++) {
      steps[i].classList.toggle('is-active', i === n - 1);
    }
    currentStep = n;
    updateProgress(n);

    var stepEl = steps[n - 1];
    var radios = stepEl.querySelectorAll('input[type="radio"]');
    var fieldName = radios.length > 0 ? radios[0].name : '';
    if (fieldName && answers[fieldName]) {
      for (var j = 0; j < radios.length; j++) {
        radios[j].checked = radios[j].value === answers[fieldName];
      }
    }

    updateNextBtn(stepEl);

    var question = stepEl.querySelector('.ame-vl-cfg__question');
    if (question) question.focus();

    announce('Step ' + n + ' of ' + TOTAL_STEPS);
    saveState();
  }

  // --- Next button enabled/disabled ---
  function updateNextBtn(stepEl) {
    var nextBtn = stepEl.querySelector('[data-vl-cfg-next]');
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (nextBtn) nextBtn.disabled = !checked;
  }

  // --- Open wizard ---
  function openWizard() {
    wizard.hidden = false;
    ctaBtn.setAttribute('aria-expanded', 'true');
    showStep(currentStep);
    requestAnimationFrame(function () {
      var rect = wizard.getBoundingClientRect();
      var headerEl = document.querySelector('#shopify-section-header') || document.querySelector('.section-header');
      var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      window.scrollTo({ top: window.scrollY + rect.top - headerH - 16, behavior: 'smooth' });
    });
  }

  // --- Close wizard ---
  function closeWizard() {
    wizard.hidden = true;
    resultsPanel.classList.remove('is-active');
    ctaBtn.setAttribute('aria-expanded', 'false');
    ctaBtn.focus();
    currentStep = 1;
    answers = {};
    clearState();
  }

  // --- Collect answer from current step ---
  function collectAnswer() {
    var stepEl = steps[currentStep - 1];
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (checked) {
      answers[checked.name] = checked.value;
    }
  }

  // --- Tag helper ---
  function hasTag(tags, tag) {
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i] === tag) return true;
    }
    return false;
  }

  // --- Scoring algorithm ---
  function scoreEquipment(product, equipment) {
    var tags = product.tags || [];
    var score = 0;
    switch (equipment) {
      case 'scooter':
        if (product.weight_capacity !== null && product.weight_capacity >= 200) score += 8;
        if (product.weight_capacity !== null && product.weight_capacity < 200) score -= 100;
        if (hasTag(tags, 'scooter-lift') || hasTag(tags, 'scooter')) score += 5;
        break;
      case 'wheelchair':
        if (product.weight_capacity !== null && product.weight_capacity >= 400) score += 10;
        else if (product.weight_capacity !== null && product.weight_capacity >= 200) score += 3;
        else if (product.weight_capacity !== null && product.weight_capacity < 200) score -= 100;
        if (hasTag(tags, 'wheelchair-lift') || hasTag(tags, 'heavy-duty')) score += 5;
        break;
      case 'heavy':
        if (product.weight_capacity !== null && product.weight_capacity >= 500) score += 12;
        else if (product.weight_capacity !== null && product.weight_capacity >= 400) score += 5;
        else if (product.weight_capacity !== null && product.weight_capacity < 400) score -= 100;
        if (hasTag(tags, 'heavy-duty') || hasTag(tags, 'bariatric')) score += 5;
        break;
    }
    return score;
  }

  function scoreMount(product, mount) {
    var tags = product.tags || [];
    var score = 0;
    switch (mount) {
      case 'hitch':
        if (hasTag(tags, 'hitch')) score += 8;
        if (hasTag(tags, 'platform')) score += 5;
        if (hasTag(tags, 'carrier')) score += 3;
        if (hasTag(tags, 'inside') || hasTag(tags, 'internal') || hasTag(tags, 'stair-lift')) score -= 5;
        break;
      case 'inside':
        if (hasTag(tags, 'inside') || hasTag(tags, 'internal')) score += 8;
        if (hasTag(tags, 'crane') || hasTag(tags, 'boom')) score += 5;
        if (hasTag(tags, 'hitch') || hasTag(tags, 'platform')) score -= 5;
        break;
      case 'stair':
        if (hasTag(tags, 'stair-lift')) score += 10;
        if (hasTag(tags, 'porch') || hasTag(tags, 'home')) score += 5;
        if (hasTag(tags, 'hitch') || hasTag(tags, 'inside')) score -= 5;
        break;
    }
    return score;
  }

  function scoreBudget(product, budget) {
    if (product.call_for_price) return 5;
    var priceDollars = product.price / 100;
    var lo = 0;
    var hi = 999999;

    switch (budget) {
      case '1500':
        lo = 0; hi = 1500;
        break;
      case '3000':
        lo = 1500; hi = 3000;
        break;
      case '9999':
        lo = 3000; hi = 999999;
        break;
    }

    if (priceDollars >= lo && priceDollars <= hi) return 10;
    if (priceDollars <= hi * 1.2) return 5;
    return 0;
  }

  function scoreProducts(ans) {
    var equipment = ans.vl_equipment || 'scooter';
    var mount = ans.vl_mount || 'hitch';
    var budget = ans.vl_budget || '9999';

    var scored = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];

      var eScore = scoreEquipment(p, equipment);
      var mScore = scoreMount(p, mount);
      var bScore = scoreBudget(p, budget);

      // Weights: equipment=4, mount=3, budget=1
      var total = (eScore * 4) + (mScore * 3) + (bScore * 1);

      scored.push({
        product: p,
        total: total,
        equipmentScore: eScore,
        mountScore: mScore,
        budgetScore: bScore
      });
    }

    scored.sort(function (a, b) { return b.total - a.total; });
    return scored;
  }

  // --- Match badge label ---
  function getMatchLabel(index) {
    switch (index) {
      case 0: return 'Best Match';
      case 1: return 'Great Fit';
      case 2: return 'Strong Pick';
      default: return 'Good Option';
    }
  }

  // --- Format price ---
  function formatPrice(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // --- Build spec pills ---
  function buildSpecs(product) {
    var specs = [];
    if (product.weight_capacity) specs.push(product.weight_capacity + ' lb capacity');
    if (product.product_weight) specs.push(product.product_weight + ' lbs');
    return specs.slice(0, 3);
  }

  // --- HTML escaping ---
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // --- Render result cards ---
  function renderResults(scored) {
    var top = [];
    for (var i = 0; i < scored.length && top.length < 5; i++) {
      if (scored[i].total > 0) top.push(scored[i]);
    }

    if (top.length === 0 && scored.length > 0) {
      for (var f = 0; f < scored.length && top.length < 3; f++) {
        top.push(scored[f]);
      }
    }

    resultsGrid.innerHTML = '';

    if (top.length === 0) {
      resultsGrid.innerHTML =
        '<div class="ame-vl-cfg__no-match">' +
          '<p>No exact matches found for your criteria.</p>' +
          '<p>Try adjusting your preferences or <a href="tel:+18039261493">call (803) 926-1493</a> for expert help.</p>' +
        '</div>';
      resultsSubtitle.textContent = '';
      announce('No exact matches found. Try adjusting your preferences or call for help.');
      return top;
    }

    if (top[0].total <= 0) {
      resultsSubtitle.textContent =
        'We couldn\'t find an exact match, but here are our closest options. ' +
        'Call (803) 926-1493 for personalized help.';
    } else {
      resultsSubtitle.textContent = 'Based on your answers, here are our top ' + top.length + ' recommendations.';
    }

    for (var j = 0; j < top.length; j++) {
      var p = top[j].product;
      var specs = buildSpecs(p);
      var specsHtml = '';
      for (var k = 0; k < specs.length; k++) {
        specsHtml += '<span class="ame-vl-cfg__card-spec">' + escapeHtml(specs[k]) + '</span>';
      }

      var priceHtml = '';
      if (p.call_for_price) {
        priceHtml = '<span style="color:var(--ame-primary)">Call for Price</span>';
      } else {
        priceHtml = formatPrice(p.price);
        if (p.compare_at_price && p.compare_at_price > p.price) {
          priceHtml += ' <span class="ame-card__compare-price">' + formatPrice(p.compare_at_price) + '</span>';
        }
      }

      var vendorHtml = p.vendor ? '<span class="ame-card__vendor">' + escapeHtml(p.vendor) + '</span>' : '';

      var card =
        '<a href="' + escapeHtml(p.url) + '" class="ame-card">' +
          '<div class="ame-card__image-wrap">' +
            '<span class="ame-vl-cfg__match-badge">' + getMatchLabel(j) + '</span>' +
            (p.image ? '<img class="ame-card__image" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="500" height="500">' : '') +
          '</div>' +
          '<div class="ame-card__body">' +
            vendorHtml +
            '<h3 class="ame-card__title">' + escapeHtml(p.title) + '</h3>' +
            '<div class="ame-vl-cfg__card-specs">' + specsHtml + '</div>' +
            '<div class="ame-card__price">' + priceHtml + '</div>' +
          '</div>' +
        '</a>';

      resultsGrid.insertAdjacentHTML('beforeend', card);
    }

    announce('Found ' + top.length + ' matching vehicle lifts. Showing your top recommendations.');
    return top;
  }

  // --- Build filter URL for "View All" ---
  function buildFilterUrl(ans) {
    var base = '/collections/vehicle-lifts';
    var params = [];

    if (ans.vl_mount) {
      switch (ans.vl_mount) {
        case 'hitch':
          params.push('filter.p.tag=hitch');
          break;
        case 'inside':
          params.push('filter.p.tag=inside');
          break;
        case 'stair':
          params.push('filter.p.tag=stair-lift');
          break;
      }
    }

    if (ans.vl_budget) {
      switch (ans.vl_budget) {
        case '1500':
          params.push('filter.v.price.lte=1500');
          break;
        case '3000':
          params.push('filter.v.price.gte=1500');
          params.push('filter.v.price.lte=3000');
          break;
        case '9999':
          params.push('filter.v.price.gte=3000');
          break;
      }
    }

    return params.length > 0 ? base + '?' + params.join('&') : base;
  }

  // --- Show results ---
  function showResults() {
    wizard.hidden = true;
    var scored = scoreProducts(answers);
    var top = renderResults(scored);

    if (viewAllLink) viewAllLink.href = buildFilterUrl(answers);

    resultsPanel.classList.add('is-active');
    if (resultsHeading) resultsHeading.focus();

    clearState();
  }

  // --- Event: CTA click ---
  ctaBtn.addEventListener('click', function () {
    if (wizard.hidden && !resultsPanel.classList.contains('is-active')) {
      openWizard();
    } else {
      closeWizard();
    }
  });

  // --- Event: radio change ---
  root.addEventListener('change', function (e) {
    if (e.target.type === 'radio' && e.target.closest('[data-vl-cfg-step]')) {
      var stepEl = e.target.closest('[data-vl-cfg-step]');
      updateNextBtn(stepEl);
    }
  });

  // --- Event: next ---
  root.addEventListener('click', function (e) {
    var nextBtn = e.target.closest('[data-vl-cfg-next]');
    if (!nextBtn || nextBtn.disabled) return;

    collectAnswer();

    if (currentStep < TOTAL_STEPS) {
      showStep(currentStep + 1);
    } else {
      showResults();
    }
  });

  // --- Event: back ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-vl-cfg-back]')) {
      collectAnswer();
      if (currentStep > 1) {
        showStep(currentStep - 1);
      }
    }
  });

  // --- Event: close ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-vl-cfg-close]')) {
      closeWizard();
    }
  });

  // --- Event: restart ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-vl-cfg-restart]')) {
      resultsPanel.classList.remove('is-active');
      answers = {};
      currentStep = 1;

      var radios = root.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        radios[i].checked = false;
      }

      clearState();
      openWizard();
    }
  });

  // --- Event: Banner CTA ---
  var bannerCta = document.querySelector('[data-cfg-banner-cta]');
  if (bannerCta) {
    bannerCta.addEventListener('click', function () {
      openWizard();
    });
  }

  // --- Event: Escape key ---
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && !wizard.hidden) {
      closeWizard();
    }
  });

  // --- Restore session ---
  var saved = loadState();
  if (saved && saved.step && saved.answers) {
    answers = saved.answers;
    currentStep = saved.step;

    var fieldNames = Object.keys(answers);
    for (var f = 0; f < fieldNames.length; f++) {
      var radio = root.querySelector('input[name="' + fieldNames[f] + '"][value="' + answers[fieldNames[f]] + '"]');
      if (radio) radio.checked = true;
    }

    openWizard();
  }
})();
